import { statuses } from 'app/pages/library/components/Constants';
import { removeAttachmentExpress } from 'lib/api';
import AttachmentPreview from 'lib/components/AttachmentPreview';
import FileProgress from 'lib/components/files/FileProgress';
import ModalAttachment from 'lib/components/modal/ModalAttachment';
import { errorToast } from 'lib/components/toasts/error';
import { useAuth, AttachedFile, FileUploadStatus } from 'lib/context';
import { useCDK } from 'lib/hooks/useCdk';
import {
  checkUploadedSize,
  MAX_ATTACHMENT_SIZE,
  StatusOptions,
  uploadAttachmentWithProgress,
} from 'lib/utils/files';
import { formatBytes } from 'lib/utils/functions';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-covideo-common';
import { BsPaperclip } from 'react-icons/bs';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';

const Container = styled.div``;

const AttachmentProgressContainer = styled.div`
  display: flex;
  align-items: center;
`;

const FileProgressContainer = styled.div`
  margin: 0 50px;
  width: 100%;
  display: flex;
  align-items: center;
`;

const AttachmentWrapper = styled.div`
  width: fit-content;
  margin-top: 16px;
`;

type Props = {
  attachments: AttachedFile[];
  setAttachments: React.Dispatch<React.SetStateAction<AttachedFile[]>>;
  attachmentUploadStatus: FileUploadStatus[];
  setAttachmentUploadStatus: React.Dispatch<
    React.SetStateAction<FileUploadStatus[]>
  >;
};

const Attachments = ({
  attachments,
  setAttachments,
  attachmentUploadStatus,
  setAttachmentUploadStatus,
}: Props) => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { secondaryToText } = useCDK();
  // attachments
  const fileRef = useRef<HTMLInputElement>(null);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const hasFilesAddon = userData.droplrAccess;
  const [cancelUpload, setCancelUpload] = useState<AbortController | null>(
    null
  );
  const [isChooseCanceled, setIsChooseCanceled] = useState(true);

  const isFilePickerDisabled =
    !hasFilesAddon &&
    !!attachmentUploadStatus?.filter(item => item.status === statuses.UPLOADING)
      .length;

  //
  // ATTACHMENTS
  // only for users without files addon
  const onChooseFile = async (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setIsChooseCanceled(false);
    const files = e.target.files;
    if (files && files.length) {
      const file = files[0];
      const uploadedSize = checkUploadedSize(attachments, MAX_ATTACHMENT_SIZE);
      if (uploadedSize.remaningSize < file.size) {
        const limit = formatBytes(MAX_ATTACHMENT_SIZE, 2);
        errorToast({
          title: t(
            'By adding this file, you will exceed the limit of {{limit}}.',
            { limit }
          ),
        });
        setIsAttachmentModalOpen(false);
        setIsChooseCanceled(true);
        return;
      }

      await onUploadAttachment(file);
    }
    setIsAttachmentModalOpen(false);
  };
  const onUploadAttachment = async (file: any) => {
    const fileIndex = attachments.length;
    const attachmentStatuses = [
      ...attachmentUploadStatus,
      {
        status: statuses.WAITING,
        currentUpload: 0,
        progress: 0,
      },
    ];

    const id = `attachments-${Math.floor(Math.random() * 1000)}`;
    let newAttachments = [
      ...attachments,
      {
        name: file.name,
        size: file.size,
        type: file.type,
        url: '', // will need to be updated
        id,
      },
    ];

    setAttachmentUploadStatus(attachmentStatuses);
    setAttachments(newAttachments);

    let itemStatuses = [...attachmentStatuses];
    const abortController = new AbortController();
    setCancelUpload(abortController);

    try {
      const uploadedFile = await uploadAttachmentWithProgress(
        file,
        (options: StatusOptions) => {
          const { status, currentUpload, progress } = options;
          itemStatuses = [...itemStatuses];
          const currentFileStatus = itemStatuses[fileIndex];

          itemStatuses[fileIndex] = {
            ...currentFileStatus,
            status: status || currentFileStatus.status,
            currentUpload: currentUpload || currentFileStatus.currentUpload,
            progress: progress || currentFileStatus.progress,
          };
          setAttachmentUploadStatus(itemStatuses);
        },
        abortController.signal
      );
      if (uploadedFile) {
        // add missing url
        newAttachments = [...newAttachments];
        newAttachments[fileIndex].url = uploadedFile.url;
        setAttachments(newAttachments);
      }
    } catch (e) {}
  };

  const onRemoveAttachment = async (id: string, fileName: string) => {
    let filteredAttachments = [...attachments];
    const index = attachments.findIndex(item => item.id === id);

    if (index === -1) return;

    filteredAttachments.splice(index, 1);
    setAttachments(filteredAttachments);

    if (!hasFilesAddon) {
      // reset statuses
      let filteredStatuses = [...attachmentUploadStatus];
      filteredStatuses.splice(index, 1);
      setAttachmentUploadStatus(filteredStatuses);
      // cancel upload
      if (cancelUpload) cancelUpload.abort();
      setIsAttachmentModalOpen(false);
    }

    try {
      await removeAttachmentExpress(fileName);
    } catch (e) {
      errorToast({ title: t('There was an error removing your attachment') });
    }
  };

  useEffect(() => {
    if (isAttachmentModalOpen && !hasFilesAddon && fileRef && fileRef.current) {
      // open file picker
      fileRef.current.click();
    }
  }, [isAttachmentModalOpen]);

  useEffect(() => {
    if (isAttachmentModalOpen && isChooseCanceled && !hasFilesAddon)
      setIsAttachmentModalOpen(false);
  }, [isAttachmentModalOpen, isChooseCanceled]);

  return (
    <Container>
      <>
        <AttachmentWrapper>
          <Button
            variant={secondaryToText}
            disabled={isFilePickerDisabled}
            text={t('Add Attachments')}
            onClick={() => setIsAttachmentModalOpen(true)}
            icon={<BsPaperclip size={14} />}
          />
        </AttachmentWrapper>
        {!hasFilesAddon && (
          <input
            ref={fileRef}
            type='file'
            style={{
              display: 'none',
            }}
            disabled={hasFilesAddon || isFilePickerDisabled}
            onChange={onChooseFile}
          />
        )}
      </>

      <div
        style={{
          ...(attachments.length && {
            marginTop: '18px',
          }),
        }}
      >
        {attachments.map((attachment: AttachedFile) => {
          const index = attachments.findIndex(
            attach => attachment.id === attach.id
          );
          let progress = 0;
          let status = '';

          if (index > -1 && !hasFilesAddon && attachmentUploadStatus) {
            progress = attachmentUploadStatus[index]?.progress;
            status = attachmentUploadStatus[index]?.status;
          }

          return (
            <AttachmentProgressContainer key={attachment.id}>
              <AttachmentPreview
                onRemove={onRemoveAttachment}
                isRemoveDisabled={isFilePickerDisabled}
                attachment={attachment}
                style={{ marginBottom: '16px' }}
              >
                {!hasFilesAddon && status && progress < 100 && (
                  <FileProgressContainer>
                    <FileProgress progress={progress} max={100} />
                  </FileProgressContainer>
                )}
              </AttachmentPreview>
            </AttachmentProgressContainer>
          );
        })}
      </div>
      {isAttachmentModalOpen && hasFilesAddon && (
        <ModalAttachment
          handleModalClose={() => setIsAttachmentModalOpen(false)}
          setUploadedFiles={setAttachments}
          uploadedFiles={attachments}
          setAttachmentUploadStatus={setAttachmentUploadStatus}
        />
      )}
    </Container>
  );
};

export default Attachments;
